import { Box, Button, Dialog, DialogContent, styled, TextField } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { fontFamilies } from '../../utils/fonts';
import { Auth } from 'aws-amplify';
import { useSnackbar } from 'notistack';
import { DefaultCFRASnack } from '../Snackbar';
import { ApiNames, RequestTypes } from '../../utils';
import { ProjectSpecificResourcesContext } from '../ProjectSpecificResourcesContext/Context';
import { TypographyStyle19, TypographyStyle31 } from '../Typography/StyledTypography';

const StyledButton = styled(Button)(() => ({
    alignItems: 'center',
    backgroundColor: '#002b5a',
    borderRadius: '10px',
    boxSizing: 'border-box',
    color: '#fff',
    display: 'flex',
    fontFamily: fontFamilies.GraphikBlack,
    fontSize: '15px',
    height: '49px',
    justifyContent: 'center',
    marginTop: '8px',
    maxWidth: '400px',
    textTransform: 'uppercase',
    width: '100%',
    '&:hover': {
        backgroundColor: '#001327',
    },
    '&.Mui-disabled': {
        backgroundColor: '#eeeeee',
    },
}));

interface ImpersonationPopupProps {
    open: boolean;
    onClose: () => void;
}

export const ImpersonationPopup = ({ open, onClose }: ImpersonationPopupProps) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isError, setIsError] = useState<boolean | string>(false);
    const { enqueueSnackbar } = useSnackbar();
    const ShowSnack = DefaultCFRASnack(enqueueSnackbar);
    const [isStartingSession, setIsStartingSession] = useState(false);
    const { user, sendSingleRequest } = useContext(ProjectSpecificResourcesContext);

    const queryVerifyUser = sendSingleRequest<any>?.(
        {
            config: {
                enabled: (email && email.length > 0 && isStartingSession) ? true : false,
            },
            noErrorOnNoKeyValuePairs: true,
            email,
        },
        {
            requestType: RequestTypes.GET,
            path: 'verify',
            queryKeyFirstElement: 'userVerify',
            apiName: ApiNames.UserManagement,
        },
    );

    useEffect(() => {
        if (queryVerifyUser?.isError) {
            ShowSnack('Failed to verify user. Please try again');
            setIsStartingSession(false);
            return;
        }
        if (queryVerifyUser && queryVerifyUser.isSuccess) {
            if (queryVerifyUser.data?.data?.verified) {
                window.localStorage.setItem('impersonated-email', email);
                try {
                    const startSession = async () => {
                        // Sign in again with current user credentials to get new token with clientMetadata
                        const currentUser = await Auth.currentAuthenticatedUser();
                        const username = currentUser.username;

                        // Sign in again with clientMetadata for impersonation
                        try {
                            await Auth.signIn(username, password, {
                                'impersonated-email': email
                            });
                        } catch (error) {
                            setIsStartingSession(false);
                            setIsError('Login failed');
                            window.localStorage.removeItem('impersonated-email');
                            return;
                        }
                        setIsStartingSession(false);
                        window.location.reload();
                    }

                    startSession();
                } catch (error) {
                    setIsStartingSession(false);
                    window.localStorage.removeItem('impersonated-email');
                    ShowSnack('Failed to impersonate user. Please try logout and login again');
                }
            } else {
                setIsError('User Login not found');
                setIsStartingSession(false);
                return;
            }
        }
    }, [ShowSnack, email, password, queryVerifyUser]);

    const handleStartSession = async () => {
        setIsError(false);
        setIsStartingSession(true);
    };

    return (
        <Dialog open={open} onClose={onClose} sx={{ zIndex: '4000' }}>
            <DialogContent>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '20px',
                    minWidth: '300px',
                    padding: '20px'
                }}>
                    <TypographyStyle31>Log in as User</TypographyStyle31>
                    <Box>
                        <Box sx={{ marginBottom: '10px' }}>
                            <TypographyStyle19>Enter the password for your account:</TypographyStyle19>
                            <TypographyStyle19>{user?.username}</TypographyStyle19>
                        </Box>
                        <TextField
                            fullWidth
                            label="Password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '10px'
                                }
                            }}
                            type="password"
                        />
                    </Box>
                    <div style={{ display: 'flex', width: '100%', borderBottom: '1px solid #E4DADA' }}></div>
                    <Box>
                        <TypographyStyle19 sx={{ marginBottom: '10px' }}>User ID to impersonate</TypographyStyle19>
                        <TextField
                            fullWidth
                            label="Enter User Login"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            error={!!isError}
                            helperText={isError ? isError : ""}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '10px'
                                }
                            }}
                        />
                    </Box>
                    <StyledButton
                        onClick={handleStartSession}
                        disabled={!email || isStartingSession}
                    >
                        {isStartingSession ? 'Connecting...' : 'Connect'}
                    </StyledButton>
                    <StyledButton
                        onClick={onClose}
                        sx={{
                            backgroundColor: '#eeeeee',
                            color: '#002b5a',
                            '&:hover': {
                                backgroundColor: '#dddddd'
                            }
                        }}
                    >
                        Cancel
                    </StyledButton>
                </Box>
            </DialogContent>
        </Dialog>
    );
};
